// Suppliers (backend services/supplier.service.ts, utils/supplier.util.ts; mounted at /api/suppliers).

/** One supplier as typed: in the form or in a row of the Excel file. Lists take a code or a name. */
export interface SupplierInput {
  name: string;
  foreignName: string;
  vat: string;
  crn: string;
  group: string;
  currency: string;
  paymentTerms: string;
  country: string;
  city: string;
  district: string;
  street: string;
  buildingNo: string;
  postalCode: string;
  mobile: string;
  phone: string;
  email: string;
  contactPerson: string;
}

export const EMPTY_SUPPLIER: SupplierInput = {
  name: '', foreignName: '', vat: '', crn: '', group: '', currency: '', paymentTerms: '', country: '', city: '',
  district: '', street: '', buildingNo: '', postalCode: '', mobile: '', phone: '', email: '', contactPerson: '',
};

export interface CodeName<C = string> { code: C; name: string }

export interface SupplierConfig {
  company: {
    companyId: number;
    name: string;
    databaseName: string;
    vatRegistered: boolean;
    vatCountry: string | null;
    /** The VAT number format in words, e.g. "15 digits, first and last digit 3". */
    vatRule: string | null;
  };
  groups: Array<CodeName<number>>;
  currencies: Array<CodeName<string>>;
  paymentTerms: Array<CodeName<number>>;
  countries: Array<CodeName<string>>;
  defaults: { groupCode: number | null; currency: string | null; payTermsCode: number | null; country: string | null };
  /** How SAP numbers new vendors (the series of the newest vendors; manual = PRPO works out the code). */
  numbering: { series: number | null; seriesName: string | null; manual: boolean };
  priceListNum: number | null;
  maxLength: Partial<Record<keyof SupplierInput, number>>;
  /** SAP user fields that are mandatory without a default: SAP would refuse a vendor PRPO creates. */
  blockingFields: string[];
  maxRows: number;
}

export interface ExistingVendor { cardCode: string; cardName: string; active: boolean }

export interface SupplierCheck {
  /** The typed VAT number, digits only. */
  digits: string;
  formatOk: boolean;
  formatMessage: string | null;
  /** Why the supplier can't be created; null when it can. */
  block: string | null;
  existing: ExistingVendor | null;
  warnings: string[];
}

export type SupplierSource = 'Form' | 'Excel' | 'Claim';

export interface CreatedSupplier {
  cardCode: string;
  cardName: string;
  vat: string;
  warnings: string[];
  creationId: number;
}

export type RowStatus = 'Ready' | 'Warning' | 'Error' | 'AlreadyInSap' | 'DuplicateInFile';

export interface ValidatedRow {
  rowNum: number;
  status: RowStatus;
  messages: string[];
  existing: ExistingVendor | null;
  vatDigits: string;
  name: string;
  vatShown: string;
  crn: string;
}

export interface CreationRow {
  creationId: number;
  source: SupplierSource;
  fileName: string | null;
  rowNum: number | null;
  cardName: string;
  vat: string | null;
  status: 'Creating' | 'Created' | 'Failed';
  /** Creating for longer than 10 minutes: the creation was interrupted; check SAP. */
  interrupted: boolean;
  cardCode: string | null;
  error: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface SupplierCreator { userId: number; name: string; email: string | null; admin: boolean }
